import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import Skeleton from '@components/ui_skeleton/component';
import GenericSection from '@components/base_generic-section/component';
import CategoryIntroduction from '@components/sec_category-introduction/component';
import SearchResults from '@components/sec_search-results/component';
import MetaTags from '../seo/MetaTags/component';
import StructuredData from '../seo/StructuredData/component';
import { CategoryModel } from '@utils/models';
import LogoutNotification from '@components/ui_logout-notification/component';
import { isBrowser } from '@utils';
import store from '@utils/store';
import { fetchCategoryRequests } from '@utils/routes';
import { t } from '@utils/i18n';
import { useUpdateEffect } from '@utils/hooks';


const CategoryPage = (props) => {
  const fetchCategory = () => {
    const requests = fetchCategoryRequests({ path: props.location.pathname, includeComponent: true });
    Promise.all(requests).then(setData);
  };

  // Set the default category as soon as possible
  let serverCategory;
  const serverHasContext = !!props?.staticContext?.data;
  if (serverHasContext) {
    serverCategory = new CategoryModel(props?.staticContext?.data, props.location.pathname);
  }

  /**
   * Check if the server has provided data
   * else fetch fresh content.
   */
  const [loaded, setLoaded] = useState(serverHasContext);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (isBrowser) {
      if (window.__INITIAL_DATA__) {
        setData(window.__INITIAL_DATA__);
        delete window.__INITIAL_DATA__;
      } else {
        fetchCategory();
      }
    }
  }, []);

  /**
   * Listen to changes to the data state
   * and set content if it has a value.
   */
  const [category, setCategory] = useState(serverCategory || {});
  useEffect(() => {
    if (data) {
      setCategory(new CategoryModel(data, props.location.pathname));
    }
  }, [data]);

  useEffect(() => {
    if (!loaded && category.id !== undefined) {
      setLoaded(true);
    }
  }, [category]);

  /**
   * Listen to any changes to the params.id
   * and refetch content if necessary.
   */
  useUpdateEffect(() => {
    setLoaded(false);
    fetchCategory();
  }, [props.match?.url]);


  if (!loaded) {
    return (
      <div className="container">
        <Skeleton/>
      </div>
    );
  }

  if (!category.id) {
    return <Redirect to="/"/>;
  }

  return (
    <>
      <MetaTags
        page="category"
        routeId={props.routeId}
        title={category.pageTitle}
        description={category.pageDescription}
        additionalTags={[
          { name: 'robots', content: category.robots },
        ]}
      />
      <StructuredData type="breadcrumbs" breadcrumbs={category.breadcrumbs} />

      {category.dynamicHeaderComponent && (
        <GenericSection store={store} {...category.dynamicHeaderComponent} />
      )}
      <div className="container">
        <LogoutNotification/>
        <div spacing="m">

          {category.hasComponents ? (
            <>
              {category.components.map((component, index) => (
                <GenericSection
                  key={index}
                  store={store}
                  {...component}
                />
              ))}
            </>
          ) : (
            <>
              {category.id && (
                <SearchResults initialCategory={category.headerTitle} store={store} pageType="category" categoryID={category.id} {...props} redirect/>
              )}

              {(category.subCategories && category.subCategories.length > 0) && (
                <GenericSection
                  store={store}
                  title={t('category.list.title')}
                  components={[
                    {
                      'component-type': 'genreList',
                      categories: category.subCategories,
                    },
                  ]}
                />
              )}
            </>
          )}
        </div>
      </div>

      {!!category.description && (
        <CategoryIntroduction
          title={category.name}
          description={category.description}
          displayTitle={true}
          displayDescription={true}
          overlap
          isBook={category.isBook}
        />
      )}

    </>
  );
};

CategoryPage.propTypes = {
  staticContext: PropTypes.object,
  match: PropTypes.object,
  location: PropTypes.object,
  routeId: PropTypes.string,
};

export default CategoryPage;
